import React, { useState } from 'react';
import { XMarkIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { retailSaleApi, RetailSale } from '../../utils/retailSalesApi';
import { compressImage, validateImageFile } from '../../utils/imageCompression';
import { getErrorMessage } from '../../utils/api';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface PaymentDepositModalProps {
  sale: RetailSale;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PaymentDepositModal: React.FC<PaymentDepositModalProps> = ({
  sale,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [paymentMethod, setPaymentMethod] = useState<'bank' | 'upi'>('bank');
  const [depositDate, setDepositDate] = useState(new Date().toISOString().split('T')[0]);
  const [depositSlipImage, setDepositSlipImage] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [compressing, setCompressing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    const validation = validateImageFile(file);
    if (!validation.valid) {
      setError(validation.error || 'Invalid image file');
      return;
    }

    try {
      setError('');
      setCompressing(true);
      setImageFile(file);
      
      // Compress image
      const compressedBase64 = await compressImage(file);
      setDepositSlipImage(compressedBase64);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to process image'));
      setImageFile(null);
      setDepositSlipImage(null);
    } finally {
      setCompressing(false);
    }
  };

  const removeImage = () => {
    setDepositSlipImage(null);
    setImageFile(null);
    const fileInput = document.getElementById('deposit-slip-input') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!depositSlipImage) {
      setError('Please upload deposit slip image');
      return;
    }

    if (!depositDate) {
      setError('Please select deposit date');
      return;
    }

    try {
      setSubmitting(true);
      setError('');

      await retailSaleApi.updateStatus(sale.id, {
        status: 'deposit',
        payment_method: paymentMethod,
        deposit_slip_image: depositSlipImage,
        deposit_date: depositDate,
      });

      onSuccess();
      onClose();
      
      // Reset form
      setPaymentMethod('bank');
      setDepositDate(new Date().toISOString().split('T')[0]);
      setDepositSlipImage(null);
      setImageFile(null);
    } catch (err: any) {
      setError(getErrorMessage(err, 'Failed to mark payment as deposited'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const saleAmount = sale.sale_type === 'on_farm' 
    ? (sale.total_amount || 0)
    : (sale.net_amount || 0);

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Mark Payment Deposited
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Sale Info */}
            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm">
                <div className="font-medium text-gray-900">Sale: {sale.retail_sale_number}</div>
                <div className="text-gray-600">Amount: ₹{saleAmount.toFixed(2)}</div>
                <div className="text-gray-600">Seller: {sale.seller_name}</div>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            )}

            {/* Form */}
            <div className="space-y-4">
              {/* Payment Method */}
              <div>
                <label className="form-label">Payment Method <span className="text-red-500">*</span></label>
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('bank')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      paymentMethod === 'bank'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">Bank Deposit</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPaymentMethod('upi')}
                    className={`p-3 rounded-lg border-2 transition-colors ${
                      paymentMethod === 'upi'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="font-medium text-gray-900">UPI</div>
                  </button>
                </div>
              </div>

              {/* Deposit Date */}
              <div>
                <label className="form-label">Deposit Date <span className="text-red-500">*</span></label>
                <input
                  type="date"
                  value={depositDate}
                  onChange={(e) => setDepositDate(e.target.value)}
                  className="form-input"
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>

              {/* Deposit Slip Image */}
              <div>
                <label className="form-label">
                  Deposit Slip Image <span className="text-red-500">*</span>
                </label>
                {!depositSlipImage ? (
                  <div className="mt-2">
                    <label
                      htmlFor="deposit-slip-input"
                      className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      {compressing ? (
                        <div className="flex flex-col items-center">
                          <LoadingSpinner size="sm" />
                          <span className="mt-2 text-sm text-gray-600">Compressing...</span>
                        </div>
                      ) : (
                        <>
                          <PhotoIcon className="h-8 w-8 text-gray-400" />
                          <span className="mt-2 text-sm text-gray-600">
                            Click to upload deposit slip
                          </span>
                          <span className="mt-1 text-xs text-gray-500">
                            JPG, PNG (max 10MB, will be compressed)
                          </span>
                        </>
                      )}
                      <input
                        id="deposit-slip-input"
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                        disabled={compressing}
                      />
                    </label>
                  </div>
                ) : (
                  <div className="mt-2">
                    <div className="relative">
                      <img
                        src={depositSlipImage}
                        alt="Deposit slip"
                        className="w-full h-48 object-contain border border-gray-200 rounded-lg bg-gray-50"
                      />
                      <button
                        type="button"
                        onClick={removeImage}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                      >
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    </div>
                    {imageFile && (
                      <p className="mt-1 text-xs text-gray-500">
                        File: {imageFile.name} ({(imageFile.size / 1024).toFixed(1)} KB)
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              onClick={handleSubmit}
              disabled={submitting || !depositSlipImage || !depositDate}
              className="w-full inline-flex justify-center rounded-lg border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <span className="flex items-center">
                  <LoadingSpinner size="sm" className="mr-2" />
                  Processing...
                </span>
              ) : (
                'Mark as Deposited'
              )}
            </button>
            <button
              onClick={onClose}
              disabled={submitting}
              className="mt-3 w-full inline-flex justify-center rounded-lg border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentDepositModal;

